import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authMe, authLogout, authExchangeSession } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await authMe();
      setUser(me);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 1) If Emergent redirected us back with a session_id fragment, consume it.
    let cancelled = false;
    const consumeFragment = async () => {
      const hash = window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      if (match) {
        try {
          const u = await authExchangeSession(decodeURIComponent(match[1]));
          if (!cancelled) setUser(u);
        } catch (e) {
          console.error('Auth exchange failed', e);
        } finally {
          // Clear the fragment so we don't re-consume it on reload
          const clean = window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, clean);
        }
      }
      await refresh();
    };
    consumeFragment();
    return () => { cancelled = true; };
  }, [refresh]);

  const login = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const logout = async () => {
    await authLogout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
